import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import { Download, Loader2, AlertTriangle, FolderOpen, HardDrive, CheckCircle, Sparkles } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { detectMimeType, getExtensionFromMime } from '@/utils/imageUtils';

interface Job {
  id: string;
  job_name: string;
  images_generated: number;
  downloaded_at: string | null;
}

interface OrganizedDownloadSelectorProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  jobs: Job[];
  onJobsDownloaded?: (jobIds: string[]) => Promise<void>;
}

const SOFT_LIMIT = 500; // Show warning
const HARD_LIMIT = 2000; // Prevent download
const AVG_IMAGE_SIZE_MB = 0.4; // ~400KB per image estimate
const BATCH_SIZE = 5; // Smaller batches for memory efficiency

export const OrganizedDownloadSelector = ({
  open,
  onOpenChange,
  jobs,
  onJobsDownloaded
}: OrganizedDownloadSelectorProps) => {
  const [selectedJobs, setSelectedJobs] = useState<Set<string>>(new Set());
  const [downloading, setDownloading] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0, job: '' });

  const jobsWithImages = jobs.filter(j => j.images_generated > 0);

  // Calculate totals
  const selectedJobsList = jobsWithImages.filter(j => selectedJobs.has(j.id));
  const totalImages = selectedJobsList.reduce((sum, j) => sum + j.images_generated, 0);
  const estimatedSizeMB = Math.round(totalImages * AVG_IMAGE_SIZE_MB);

  // Reset selection when dialog opens
  useEffect(() => {
    if (open) {
      setSelectedJobs(new Set());
      setProgress({ current: 0, total: 0, job: '' });
    }
  }, [open]);

  const toggleJob = (jobId: string) => {
    const newSelected = new Set(selectedJobs);
    if (newSelected.has(jobId)) {
      newSelected.delete(jobId);
    } else {
      newSelected.add(jobId);
    }
    setSelectedJobs(newSelected);
  };

  const selectAll = () => {
    setSelectedJobs(new Set(jobsWithImages.map(j => j.id)));
  };

  const selectNotDownloaded = () => {
    const notDownloadedIds = jobsWithImages
      .filter(j => !j.downloaded_at)
      .map(j => j.id);
    setSelectedJobs(new Set(notDownloadedIds));
  };

  const clearAll = () => {
    setSelectedJobs(new Set());
  };

  const notDownloadedCount = jobsWithImages.filter(j => !j.downloaded_at).length;

  const handleDownload = async () => {
    if (totalImages > HARD_LIMIT) {
      toast.error(`Too many images (${totalImages}). Maximum is ${HARD_LIMIT}. Please select fewer jobs.`);
      return;
    }

    if (selectedJobs.size === 0) {
      toast.error('Please select at least one job');
      return;
    }

    setDownloading(true);
    const toastId = toast.loading('Preparing organized download...');

    try {
      const JSZip = (await import('jszip')).default;
      const zip = new JSZip();
      let totalSuccess = 0;
      let processedImages = 0;

      setProgress({ current: 0, total: totalImages, job: '' });

      // Process jobs one at a time to manage memory
      for (const job of selectedJobsList) {
        const folderName = job.job_name
          .replace(/^\d+[.\s-]+/, '')
          .replace(/[–—]/g, '-')
          .replace(/[^a-zA-Z0-9_-]/g, '_');
        
        const jobFolder = zip.folder(folderName);
        setProgress(p => ({ ...p, job: job.job_name }));

        // Get image IDs for this job
        const { data: imageIds, error: idsError } = await supabase
          .from('generated_images')
          .select('id')
          .eq('automated_job_id', job.id);

        if (idsError || !imageIds?.length) {
          console.warn(`Failed to get images for job ${job.job_name}:`, idsError);
          continue;
        }

        let jobImageCount = 0;

        // Process in small batches to prevent memory exhaustion
        for (let i = 0; i < imageIds.length; i += BATCH_SIZE) {
          const batchIds = imageIds.slice(i, i + BATCH_SIZE).map(img => img.id);
          
          // Just fetch b64_json - it contains final (enhanced) image
          const { data: batchImages, error: batchError } = await supabase
            .from('generated_images')
            .select('id, b64_json')
            .in('id', batchIds);

          if (batchError) {
            console.warn(`Batch fetch error:`, batchError);
            continue;
          }

          // Process each image in the batch
          for (const image of batchImages || []) {
            try {
              if (!image.b64_json || image.b64_json.length < 100) continue;

              const mimeType = detectMimeType(image.b64_json);
              const ext = getExtensionFromMime(mimeType);
              
              const binaryString = atob(image.b64_json);
              const bytes = new Uint8Array(binaryString.length);
              for (let j = 0; j < binaryString.length; j++) {
                bytes[j] = binaryString.charCodeAt(j);
              }

              jobFolder?.file(`${folderName}_${++jobImageCount}.${ext}`, bytes);
              totalSuccess++;
              processedImages++;

              // Clear reference for garbage collection
              (image as any).b64_json = null;
            } catch (err) {
              console.error('Failed to process image:', err);
            }
          }

          // Update progress
          setProgress(p => ({ ...p, current: processedImages }));
          toast.loading(`Processing: ${processedImages}/${totalImages} images...`, { id: toastId });

          // Allow garbage collection between batches
          await new Promise(r => setTimeout(r, 20));
        }
      }

      if (totalSuccess === 0) {
        toast.error('No valid images found to download', { id: toastId });
        return;
      }

      toast.loading('Generating ZIP file...', { id: toastId });

      const zipBlob = await zip.generateAsync({
        type: 'blob',
        compression: 'DEFLATE',
        compressionOptions: { level: 1 }
      }, (metadata) => {
        toast.loading(`Generating ZIP: ${metadata.percent.toFixed(0)}%`, { id: toastId });
      });

      const link = document.createElement('a');
      link.href = URL.createObjectURL(zipBlob);
      link.download = `jobs_organized_${new Date().toISOString().split('T')[0]}.zip`;
      link.click();
      URL.revokeObjectURL(link.href);

      // Mark jobs as downloaded
      if (onJobsDownloaded) {
        try {
          await onJobsDownloaded(Array.from(selectedJobs));
        } catch (err) {
          console.error('Failed to mark jobs as downloaded:', err);
        }
      }

      toast.success(`Downloaded ${totalSuccess} images in ${selectedJobsList.length} folders`, { id: toastId });
      onOpenChange(false);
    } catch (error) {
      console.error('Error creating organized ZIP:', error);
      toast.error('Failed to create ZIP. Try selecting fewer jobs.', { id: toastId });
    } finally {
      setDownloading(false);
      setProgress({ current: 0, total: 0, job: '' });
    }
  };

  const isOverSoftLimit = totalImages > SOFT_LIMIT;
  const isOverHardLimit = totalImages > HARD_LIMIT;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg bg-background border-border">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FolderOpen className="h-5 w-5" />
            Download Jobs as Organized ZIP
          </DialogTitle>
          <DialogDescription>
            Select which jobs to include in the download. Each job will be in its own folder.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Summary Stats */}
          <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
            <div className="flex items-center gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold">{selectedJobs.size}</div>
                <div className="text-xs text-muted-foreground">Jobs</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold">{totalImages}</div>
                <div className="text-xs text-muted-foreground">Images</div>
              </div>
              <div className="text-center flex items-center gap-1">
                <HardDrive className="h-4 w-4 text-muted-foreground" />
                <div>
                  <div className="text-lg font-bold">~{estimatedSizeMB} MB</div>
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={selectAll}>
                Select All
              </Button>
              {notDownloadedCount > 0 && (
                <Button variant="outline" size="sm" onClick={selectNotDownloaded} className="text-primary">
                  <Sparkles className="h-3 w-3 mr-1" />
                  New ({notDownloadedCount})
                </Button>
              )}
              <Button variant="ghost" size="sm" onClick={clearAll}>
                Clear
              </Button>
            </div>
          </div>

          {/* Warning Messages */}
          {isOverHardLimit && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-destructive/10 border border-destructive/30 text-destructive">
              <AlertTriangle className="h-5 w-5 flex-shrink-0" />
              <div className="text-sm">
                <strong>Too many images!</strong> Maximum is {HARD_LIMIT} images. Please deselect some jobs.
              </div>
            </div>
          )}

          {isOverSoftLimit && !isOverHardLimit && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/30 text-yellow-500">
              <AlertTriangle className="h-5 w-5 flex-shrink-0" />
              <div className="text-sm">
                <strong>Large download:</strong> This may take several minutes and use significant memory.
              </div>
            </div>
          )}

          {/* Job List */}
          <ScrollArea className="h-[300px] rounded-md border p-2">
            <div className="space-y-2">
              {jobsWithImages.map(job => (
                <label
                  key={job.id}
                  className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-colors ${
                    selectedJobs.has(job.id)
                      ? 'bg-primary/10 border border-primary/30'
                      : 'bg-muted/30 border border-transparent hover:bg-muted/50'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <Checkbox
                      checked={selectedJobs.has(job.id)}
                      onCheckedChange={() => toggleJob(job.id)}
                    />
                    <div>
                      <div className="font-medium text-sm flex items-center gap-2">
                        {job.job_name}
                        {job.downloaded_at && (
                          <Badge variant="outline" className="text-green-600 border-green-600 text-[10px] px-1.5 py-0">
                            <CheckCircle className="h-2.5 w-2.5 mr-0.5" />
                            Downloaded
                          </Badge>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {job.images_generated} images • ~{Math.round(job.images_generated * AVG_IMAGE_SIZE_MB)} MB
                      </div>
                    </div>
                  </div>
                  <Badge variant="secondary">{job.images_generated}</Badge>
                </label>
              ))}
            </div>
          </ScrollArea>

          {/* Download Progress */}
          {downloading && progress.total > 0 && (
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Processing: {progress.job}</span>
                <span>{progress.current}/{progress.total}</span>
              </div>
              <div className="h-2 bg-muted rounded-full overflow-hidden">
                <div 
                  className="h-full bg-primary transition-all duration-300"
                  style={{ width: `${(progress.current / progress.total) * 100}%` }}
                />
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={downloading}>
            Cancel
          </Button>
          <Button 
            onClick={handleDownload} 
            disabled={downloading || selectedJobs.size === 0 || isOverHardLimit}
          >
            {downloading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Downloading...
              </>
            ) : (
              <>
                <Download className="h-4 w-4 mr-2" />
                Download Selected
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
